import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useKodiakFarmLockedStakesOf } from 'hooks/useKodiakFarm'
import { KodiakFarmStake } from 'hooks/useKodiakFarm'
import { useRewardVaultDatas } from 'hooks/useRewardVault'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { KodiakFarm } from 'types/farms'
import { Address } from 'viem'

import { REFETCH_POLICY_FAST } from '../../../config/RefetchPolicies'
import * as S from '../Styleds'
import { useIslandContext } from './hooks/useIslandContext'
import StakeView from './StakeView'
import UnstakeView from './UnstakeView'

export interface SharedProps {
  farm: KodiakFarm | null | undefined
  rewardVault?: Address
  token: Token | null | undefined
  lpBalance: CurrencyAmount<Token> | null | undefined
  totalLiquidity: CurrencyAmount<Token> | null | undefined
  avaliableStakes: KodiakFarmStake[] | undefined
  stakes: any[] | null | undefined
  stakingType: 'farm' | 'vault' | 'none'
  showMigrationOption?: boolean
}

interface IslandManagementCardProps {
  farm: KodiakFarm | null | undefined
  rewardVault?: Address
  stakingType: 'farm' | 'vault' | 'none'
  showMigrationOption?: boolean
}

const IslandManagementCard = ({
  farm,
  rewardVault,
  stakingType = 'farm',
  showMigrationOption = false,
}: IslandManagementCardProps) => {
  const [direction, setDirection] = useState<'stake' | 'unstake'>('stake')
  const { account } = useConnection()
  const context = useIslandContext()

  const { data: vaultData, isLoading: isLoadingVault } = useRewardVaultDatas(
    stakingType === 'vault' ? rewardVault || undefined : undefined,
    account
  )

  // Token - get from either farm or vault
  const token = useToken(vaultData ? vaultData.stakingToken : farm?.stakingToken.id)

  // User balances
  const lpBalance = useTokenBalance(account, token || undefined, REFETCH_POLICY_FAST)

  // Get farm data if needed
  const { data: farmData } = useKodiakFarmLockedStakesOf(stakingType === 'farm' ? farm : null, account)

  // Derive the positions/stakes from either farm or vault data
  const stakes = useMemo(() => {
    if (stakingType === 'farm' && farmData) {
      return farmData.stakes
    } else if (stakingType === 'vault' && vaultData) {
      return vaultData.positions
    }
    return undefined
  }, [stakingType, farmData, vaultData])

  // Get the total liquidity from either source
  const totalLiquidity = useMemo(() => {
    if (stakingType === 'farm' && farmData) {
      return farmData.totalLiquidity
    } else if (stakingType === 'vault' && vaultData) {
      return vaultData.selfStaked
    }
    return undefined
  }, [stakingType, farmData, vaultData])

  // Check if user has staked tokens
  const hasStakedBalance = useMemo(() => {
    return totalLiquidity && totalLiquidity.greaterThan('0')
  }, [totalLiquidity])

  // Auto-switch tab if conditions not met for current tab
  useEffect(() => {
    if (!hasStakedBalance && direction === 'unstake') {
      setDirection('stake')
    }

    if (!lpBalance || lpBalance.equalTo('0')) {
      setDirection('unstake')
    }
  }, [hasStakedBalance, lpBalance, direction])

  // Combined props for child components
  const sharedProps = useMemo(() => {
    return {
      farm: stakingType === 'farm' ? farm : null,
      rewardVault: stakingType === 'vault' ? rewardVault : undefined,
      token,
      lpBalance,
      totalLiquidity,
      avaliableStakes: farmData?.avaliableStakes,
      stakes,
      stakingType,
      showMigrationOption,
    }
  }, [
    farm,
    rewardVault,
    token,
    lpBalance,
    totalLiquidity,
    farmData?.avaliableStakes,
    stakes,
    stakingType,
    showMigrationOption,
  ])

  // Get appropriate label for stake/unstake based on stakingType
  const getStakingLabel = useCallback(
    (baseLabel: string) => {
      if (stakingType === 'vault') {
        return baseLabel === 'Stake' ? 'Stake' : 'Unstake'
      }
      return baseLabel
    },
    [stakingType]
  )

  // Render the appropriate view
  const render = useCallback(() => {
    switch (direction) {
      case 'stake':
        return <StakeView {...sharedProps} />
      case 'unstake':
        return <UnstakeView {...sharedProps} />
      default:
        return <>Error Direction</>
    }
  }, [direction, sharedProps])

  // Don't show anything if neither farm nor vault is active
  if (stakingType === 'none') {
    return null
  }

  return (
    <S.IslandManagementCard>
      <S.DepositWithdrawSwitch>
        <>
          {/* Show stake button if user has tokens to stake */}
          {lpBalance && lpBalance.greaterThan('0') && (
            <S.DepositWithdrawOption onClick={() => setDirection('stake')} active={direction === 'stake'} tabIndex={-1}>
              <>{getStakingLabel('Stake')}</>
            </S.DepositWithdrawOption>
          )}

          {/* Show unstake button if user has staked tokens */}
          {hasStakedBalance && (
            <S.DepositWithdrawOption
              onClick={() => setDirection('unstake')}
              active={direction === 'unstake'}
              tabIndex={-1}
            >
              <>{getStakingLabel('Unstake')}</>
            </S.DepositWithdrawOption>
          )}

          {/* If neither condition is met, show a disabled message */}
          {(!lpBalance || !lpBalance.greaterThan('0')) && !hasStakedBalance && (
            <S.DepositWithdrawOption active={true} disabled={true}>
              <>No tokens to stake or unstake</>
            </S.DepositWithdrawOption>
          )}
        </>
      </S.DepositWithdrawSwitch>
      {render()}
    </S.IslandManagementCard>
  )
}

export default IslandManagementCard
